import { z } from 'zod';
import { civilDateAt, isCivilDate } from '@/shared/format/date';
import { bankSlipInputDate, bankSlipWireDate } from './bank-slip.presentation';

import type { BankSlipAction } from './bank-slip.status-actions';
import type { BankSlip } from './bank-slip.wire';

export type BankSlipFormValues = Record<string, unknown>;
export const otherInformationFields = [
  'discount',
  'discountLimitDate',
  'documentNumber',
  'fineAmount',
  'fineTypeValue',
  'numberPayments',
  'payer',
  'guarantor',
  'interestTypeValue',
  'interestAmount',
  'firstInstructionDebtCollection',
  'secondInstructionDebtCollection',
  'description',
  'interestCodeValue',
] as const;
const optionalString = (value: unknown) =>
  value == null
    ? value
    : typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean'
      ? String(value)
      : undefined;

export function bankSlipFormDefaults(
  action: BankSlipAction,
  bankSlip: BankSlip,
  now: number,
): BankSlipFormValues {
  if (action === 'updateOtherInformation')
    return {
      ...bankSlip,
      discountLimitDate:
        bankSlipInputDate(bankSlip.discountLimitDate) ?? bankSlip.discountLimitDate,
      originalDiscountLimitDate: bankSlip.discountLimitDate,
      payer: bankSlip.payer ? { ...bankSlip.payer } : null,
      guarantor: bankSlip.guarantor ? { ...bankSlip.guarantor } : null,
    };
  return {
    dueDate: null,
    amount: null,
    deduction: null,
    toWalletCode: null,
    keepBillingOpen: action === 'cancelProtest',
    paidAmount: null,
    confirmationTransactionTypeValue: null,
    paymentISPB: null,
    paymentAgency: null,
    paymentDate: civilDateAt(new Date(now)),
    originalPaymentDate: new Date(now).toISOString(),
    walletCodeType: 'originWalletCode',
    useUpdated: false,
  };
}
export function bankSlipFormSchema(action: BankSlipAction, required: string, days: string) {
  return z.record(z.string(), z.unknown()).superRefine((values, ctx) => {
    const requiredFields: Partial<Record<BankSlipAction, string[]>> = {
      updateDueDate: ['dueDate'],
      updateAmount: ['amount'],
      insertDeduction: ['deduction'],
      transfer: ['toWalletCode'],
      settlement: ['paidAmount', 'confirmationTransactionTypeValue'],
    };
    for (const field of requiredFields[action] ?? []) {
      if (values[field] == null || values[field] === '')
        ctx.addIssue({ code: 'custom', path: [field], message: required });
    }
    const dateField =
      action === 'updateDueDate'
        ? 'dueDate'
        : action === 'settlement'
          ? 'paymentDate'
          : action === 'updateOtherInformation'
            ? 'discountLimitDate'
            : null;
    if (
      dateField &&
      values[dateField] != null &&
      (typeof values[dateField] !== 'string' || !isCivilDate(values[dateField]))
    )
      ctx.addIssue({ code: 'custom', path: [dateField], message: required });
    if (action === 'updateOtherInformation' && values['firstInstructionDebtCollection'] === '06') {
      const count = Number(values['secondInstructionDebtCollection']);
      if (!Number.isFinite(count) || count < 3 || count > 30)
        ctx.addIssue({ code: 'custom', path: ['secondInstructionDebtCollection'], message: days });
    }
    if (action === 'settlement')
      for (const field of ['paymentISPB', 'paymentAgency']) {
        if (
          values[field] != null &&
          (values[field] === '' || !Number.isFinite(Number(values[field])))
        )
          ctx.addIssue({ code: 'custom', path: [field], message: required });
      }
    if (
      action === 'updateOtherInformation' &&
      values['secondInstructionDebtCollection'] != null &&
      (values['secondInstructionDebtCollection'] === '' ||
        !Number.isFinite(Number(values['secondInstructionDebtCollection'])))
    )
      ctx.addIssue({ code: 'custom', path: ['secondInstructionDebtCollection'], message: days });
  });
}
export function bankSlipFormDate(value: unknown, original?: unknown) {
  if (typeof value !== 'string') return null;
  if (typeof original === 'string' && bankSlipInputDate(original) === value)
    return original;
  return bankSlipWireDate(value) ?? null;
}
export function otherInformationBody(values: BankSlipFormValues) {
  return Object.fromEntries(
    otherInformationFields.map((field) => {
      const value = values[field];
      if (field === 'discountLimitDate')
        return [
          field,
          value === undefined
            ? undefined
            : bankSlipFormDate(value, values['originalDiscountLimitDate']),
        ];
      if (field === 'payer' || field === 'guarantor') return [field, value];
      if (field === 'secondInstructionDebtCollection')
        return [field, value == null ? value : Number(value)];
      return [field, optionalString(value)];
    }),
  );
}
